import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';
import { useAuthToken } from '../hooks/useAuthToken';
import GraphService, { UserProfile } from '../services/graphService';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';

interface UserContextType {
  userProfile: UserProfile | null;
  profilePhotoUrl: string | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { accessToken, clearAllAuthState } = useAuthToken();
  
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadUserProfile = useCallback(async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    try {
      // Load profile and photo in parallel
      const [profile, photoUrl] = await Promise.all([
        GraphService.getUserProfile(accessToken),
        GraphService.getUserPhoto(accessToken)
      ]);

      setUserProfile(profile);
      setProfilePhotoUrl(photoUrl);
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  // Load user profile and photo when access token is available
  useEffect(() => {
    if (accessToken && account) {
      loadUserProfile();
    }
  }, [accessToken, account, loadUserProfile]);

  const refreshProfile = async () => {
    await loadUserProfile();
  };

  const signOut = async () => {
    console.log('🚪 Initiating sign-out process...');

    try {
      // Step 1: Clear user data from context
      setUserProfile(null);
      setProfilePhotoUrl(null);
      setIsLoading(false);

      // Step 2: Clear all authentication state
      clearAllAuthState();

      // Step 3: Clear API client authentication
      try {
        await apiClient.logout();
        console.log('🧹 API client logout completed');
      } catch (apiError) {
        console.warn('⚠️ API client logout failed:', apiError);
        // Continue with logout process even if API client cleanup fails
      }

      // Step 5: Clear local storage and session storage
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log('🧹 Cleared local and session storage');
      } catch (storageError) {
        console.warn('⚠️ Failed to clear storage:', storageError);
      }

      // Step 6: Properly remove all MSAL accounts and clear cache (Microsoft best practice)
      try {
        console.log('🔄 Clearing MSAL accounts and cache...');
        
        // Remove all accounts from MSAL
        const allAccounts = instance.getAllAccounts();
        console.log(`🔧 Removing ${allAccounts.length} MSAL accounts`);
        
        // Clear active account first
        instance.setActiveAccount(null);
        
        // Clear the cache completely
        await instance.clearCache();
        
        // Manual cleanup of MSAL storage keys (ensures complete removal)
        const msalKeys = ['msal.account.keys', 'msal.token.keys', 'msal.interaction.status', 'msal.token.bind'];
        msalKeys.forEach(key => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
        
        // Remove any remaining MSAL entries
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('msal.')) {
            localStorage.removeItem(key);
          }
        });
        
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('msal.')) {
            sessionStorage.removeItem(key);
          }
        });
        
        console.log('✅ MSAL accounts and cache fully cleared');
      } catch (clearError) {
        console.error('❌ Failed to clear MSAL accounts:', clearError);
      }

      // Step 7: Redirect directly to login page (no Microsoft logout screens)
      console.log('✅ Sign-out completed - redirecting to login page');
      window.location.replace(window.location.origin);

    } catch (error) {
      console.error('❌ Critical error during sign-out:', error);

      // Emergency cleanup: Clear what we can and redirect
      try {
        setUserProfile(null);
        setProfilePhotoUrl(null);
        setIsLoading(false);
        clearAllAuthState();
        
        // Clear MSAL cache and accounts manually as emergency fallback
        try {
          instance.setActiveAccount(null);
          await instance.clearCache();
          
          // Manual MSAL storage cleanup
          const msalKeys = ['msal.account.keys', 'msal.token.keys', 'msal.interaction.status'];
          msalKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          });
        } catch (msalCleanupError) {
          console.warn('⚠️ MSAL emergency cleanup failed:', msalCleanupError);
        }
        
        // Clear API client tokens (synchronous call to avoid async issues)
        delete (apiClient as any).axiosInstance.defaults.headers.common['Authorization'];
        localStorage.clear();
        sessionStorage.clear();
        console.log('🧹 Emergency cleanup completed');
      } catch (cleanupError) {
        console.error('❌ Emergency cleanup failed:', cleanupError);
      }

      // Force redirect to clear all authentication state
      console.log('🔄 Emergency redirect to clear authentication state...');
      window.location.replace(window.location.origin);
    }
  };

  // No cleanup needed for data URIs

  const value: UserContextType = {
    userProfile,
    profilePhotoUrl,
    isLoading,
    signOut,
    refreshProfile
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
